import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Machine d'état explicite pour gérer les réponses vocales d'Owen
 * 
 * États possibles :
 * - IDLE : Aucune réponse en cours
 * - STREAMING : Réception du texte en streaming
 * - TTS_GENERATION : Génération des audios TTS
 * - AUDIO_PLAYING : Lecture audio en cours
 * - DONE : Réponse terminée
 * - ERROR : Erreur rencontrée
 * 
 * Transitions autorisées :
 * IDLE → STREAMING
 * STREAMING → TTS_GENERATION | ERROR
 * TTS_GENERATION → AUDIO_PLAYING | ERROR
 * AUDIO_PLAYING → DONE | ERROR
 * ERROR → IDLE
 * DONE → IDLE
 */

const PHASES = {
  IDLE: 'IDLE',
  STREAMING: 'STREAMING',
  TTS_GENERATION: 'TTS_GENERATION',
  AUDIO_PLAYING: 'AUDIO_PLAYING',
  DONE: 'DONE',
  ERROR: 'ERROR'
};

const ALLOWED_TRANSITIONS = {
  [PHASES.IDLE]: [PHASES.STREAMING],
  [PHASES.STREAMING]: [PHASES.TTS_GENERATION, PHASES.ERROR],
  [PHASES.TTS_GENERATION]: [PHASES.AUDIO_PLAYING, PHASES.ERROR],
  [PHASES.AUDIO_PLAYING]: [PHASES.DONE, PHASES.ERROR],
  [PHASES.ERROR]: [PHASES.IDLE],
  [PHASES.DONE]: [PHASES.IDLE]
};

export const useOwenStateMachine = () => {
  const [state, setState] = useState({
    phase: PHASES.IDLE,
    textBuffer: '',          // Texte accumulé pendant streaming
    textComplete: false,     // Streaming terminé ?
    ttsQueue: [],           // Queue des audios TTS à générer
    ttsReady: false,        // Tous les TTS générés ?
    audioQueue: [],         // Queue des audios prêts à jouer
    currentAudioIndex: 0,   // Index audio en cours
    displayedText: '',      // Texte affiché à l'utilisateur
    error: null,            // Erreur éventuelle
    metadata: {}            // Métadonnées (messageId, emotion, etc.)
  });
  
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  // Fonction de transition avec validation
  const transition = useCallback((newPhase, updates = {}) => {
    const currentPhase = stateRef.current.phase;
    
    // Validation de la transition
    if (!ALLOWED_TRANSITIONS[currentPhase]?.includes(newPhase)) {
      console.error(
        `[OwenStateMachine] ❌ INVALID TRANSITION: ${currentPhase} → ${newPhase}`,
        '\nAllowed:', ALLOWED_TRANSITIONS[currentPhase]
      );
      return false;
    }
    
    console.log(`[OwenStateMachine] ✅ TRANSITION: ${currentPhase} → ${newPhase}`);
    
    setState(prev => ({
      ...prev,
      phase: newPhase,
      ...updates
    }));
    
    return true;
  }, []);
  
  // Démarrer streaming
  const startStreaming = useCallback((metadata = {}) => {
    if (stateRef.current.phase !== PHASES.IDLE) {
      console.warn('[OwenStateMachine] ⚠️ Cannot start streaming - not in IDLE phase');
      return false;
    }
    
    transition(PHASES.STREAMING, {
      textBuffer: '',
      textComplete: false,
      ttsQueue: [],
      ttsReady: false,
      audioQueue: [],
      currentAudioIndex: 0,
      displayedText: '',
      error: null,
      metadata
    });
    
    return true;
  }, [transition]);
  
  // Ajouter chunk de texte
  const addTextChunk = useCallback((chunk) => {
    if (stateRef.current.phase !== PHASES.STREAMING) {
      console.warn('[OwenStateMachine] ⚠️ Cannot add chunk - not in STREAMING phase');
      return false;
    }
    
    setState(prev => ({
      ...prev,
      textBuffer: prev.textBuffer + chunk
    }));
    
    return true;
  }, []);
  
  // Terminer streaming et passer à génération TTS
  const completeStreaming = useCallback(() => {
    if (stateRef.current.phase !== PHASES.STREAMING) {
      console.warn('[OwenStateMachine] ⚠️ Cannot complete streaming - not in STREAMING phase');
      return false;
    }
    
    const fullText = stateRef.current.textBuffer;
    console.log('[OwenStateMachine] 📝 Streaming complete - Text length:', fullText.length);
    
    // Découper en phrases
    const sentences = fullText.split(/(?<=[.!?…])\s+/);
    const validSentences = sentences.filter(s => {
      const trimmed = s.trim();
      return /[.!?…]$/.test(trimmed) && trimmed.length > 3;
    });
    
    console.log('[OwenStateMachine] 📋 Detected', validSentences.length, 'sentences for TTS');
    
    transition(PHASES.TTS_GENERATION, {
      textComplete: true,
      ttsQueue: validSentences.map((text, index) => ({
        index,
        text: text.trim(),
        audioUrl: null,
        status: 'pending'  // pending | generating | ready | error
      })),
      ttsReady: false
    });
    
    return true;
  }, [transition]);
  
  // Marquer un TTS comme prêt
  const markTTSReady = useCallback((index, audioUrl) => {
    if (stateRef.current.phase !== PHASES.TTS_GENERATION) {
      console.warn('[OwenStateMachine] ⚠️ Cannot mark TTS ready - not in TTS_GENERATION phase');
      return false;
    }
    
    setState(prev => {
      const newTTSQueue = [...prev.ttsQueue];
      const item = newTTSQueue.find(t => t.index === index);
      
      if (item) {
        item.audioUrl = audioUrl;
        item.status = 'ready';
      }
      
      // Vérifier si tous les TTS sont prêts
      const allReady = newTTSQueue.every(t => t.status === 'ready' || t.status === 'error');
      
      return {
        ...prev,
        ttsQueue: newTTSQueue,
        ttsReady: allReady
      };
    });
    
    return true;
  }, []);
  
  // Démarrer lecture audio
  const startAudioPlayback = useCallback(() => {
    if (stateRef.current.phase !== PHASES.TTS_GENERATION || !stateRef.current.ttsReady) {
      console.warn('[OwenStateMachine] ⚠️ Cannot start audio - not ready');
      return false;
    }
    
    const audioQueue = stateRef.current.ttsQueue
      .filter(t => t.status === 'ready')
      .map(t => ({
        text: t.text,
        audioUrl: t.audioUrl
      }));
    
    console.log('[OwenStateMachine] 🔊 Starting audio playback -', audioQueue.length, 'items');
    
    transition(PHASES.AUDIO_PLAYING, {
      audioQueue,
      currentAudioIndex: 0,
      displayedText: ''
    });
    
    return true;
  }, [transition]);
  
  // Afficher texte progressivement (synchronisé avec audio)
  const updateDisplayedText = useCallback((text) => {
    if (stateRef.current.phase !== PHASES.AUDIO_PLAYING) {
      console.warn('[OwenStateMachine] ⚠️ Cannot update displayed text - not in AUDIO_PLAYING phase');
      return false;
    }
    
    setState(prev => ({
      ...prev,
      displayedText: text
    }));
    
    return true;
  }, []);
  
  // Passer à l'audio suivant
  const nextAudio = useCallback(() => {
    if (stateRef.current.phase !== PHASES.AUDIO_PLAYING) {
      console.warn('[OwenStateMachine] ⚠️ Cannot go to next audio - not in AUDIO_PLAYING phase');
      return false;
    }
    
    const newIndex = stateRef.current.currentAudioIndex + 1;
    
    if (newIndex >= stateRef.current.audioQueue.length) {
      // Tous les audios joués → DONE
      console.log('[OwenStateMachine] ✅ All audios played - transitioning to DONE');
      transition(PHASES.DONE, {
        displayedText: stateRef.current.textBuffer  // Afficher texte complet
      });
      return true;
    }
    
    setState(prev => ({
      ...prev,
      currentAudioIndex: newIndex
    }));
    
    return true;
  }, [transition]);
  
  // Terminer réponse
  const complete = useCallback(() => {
    if (stateRef.current.phase !== PHASES.DONE) {
      console.warn('[OwenStateMachine] ⚠️ Cannot complete - not in DONE phase');
      return false;
    }
    
    console.log('[OwenStateMachine] 🏁 Response complete - resetting to IDLE');
    transition(PHASES.IDLE);
    
    return true;
  }, [transition]);
  
  // Gérer erreur
  const error = useCallback((errorMessage) => {
    console.error('[OwenStateMachine] ❌ ERROR:', errorMessage);
    
    setState(prev => ({
      ...prev,
      phase: PHASES.ERROR,
      error: errorMessage
    }));
  }, []);
  
  // Reset à IDLE depuis ERROR
  const reset = useCallback(() => {
    if (stateRef.current.phase !== PHASES.ERROR && stateRef.current.phase !== PHASES.DONE) {
      console.warn('[OwenStateMachine] ⚠️ Cannot reset - invalid phase');
      return false;
    }
    
    transition(PHASES.IDLE);
    return true;
  }, [transition]);
  
  return {
    // État
    state,
    phase: state.phase,
    isIdle: state.phase === PHASES.IDLE,
    isStreaming: state.phase === PHASES.STREAMING,
    isGeneratingTTS: state.phase === PHASES.TTS_GENERATION,
    isPlayingAudio: state.phase === PHASES.AUDIO_PLAYING,
    isDone: state.phase === PHASES.DONE,
    isError: state.phase === PHASES.ERROR,
    
    // Actions
    startStreaming,
    addTextChunk,
    completeStreaming,
    markTTSReady,
    startAudioPlayback,
    updateDisplayedText,
    nextAudio,
    complete,
    error,
    reset,
    
    // Constantes
    PHASES
  };
};

export default useOwenStateMachine;
